import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useCallback, useEffect, useState } from 'react'
import {
  selectProcessEmailAttachmentAttachmentId,
  selectProcessEmailAttachmentEmailId,
  setProcessEmailAttachmentIsAttachmentLoading,
} from '../../../store/processDocument/emailAttachmentInfo'
import { useLoadEmailAttachmentMutation } from '../../../store/integrations/integrations'
import { selectAccountId } from '../../../store/auth/authSlice'
import { setProcessDocumentFileInfo } from '../../../store/processDocument/documentInfo'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { useDocumentSummary } from '../DocumentSummary/hooks'
import { selectDocumentTypes, selectDocumentTypesLoading } from '../../../store/processDocument/documentTypes'
import { selectDocumentSummaryExecutionLoading, selectDocumentSummaryResult, setShowDocumentSummary } from '../../../store/processDocument/documentSummary'
import { DocumentType, FALLBACK_DOCUMENT_TYPE_ID } from '../../../models/DocumentType'
import { FileInfo } from '../../../store/media/file'
import { selectProcessDocumentCurrentUserEmailIntegration } from '../../../store/processDocument/documentUsers'

export const useLoadAttachment = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const attachmentId = useAppSelector(selectProcessEmailAttachmentAttachmentId)
  const emailId = useAppSelector(selectProcessEmailAttachmentEmailId)
  const emailIntegration = useAppSelector(selectProcessDocumentCurrentUserEmailIntegration)

  const [triggerLoadAttachment] = useLoadEmailAttachmentMutation()

  const canLoadAttachment = !!emailIntegration && !!emailId && !!attachmentId

  const loadAttachment = useCallback(async () => {
    if (!canLoadAttachment) {
      return null
    }
    dispatch(setProcessEmailAttachmentIsAttachmentLoading(true))
    try {
      const fileInfo = await triggerLoadAttachment({
        accountId: accountId,
        connector: emailIntegration.serviceId,
        emailId: emailId,
        attachmentId: attachmentId,
      }).unwrap()
      dispatch(setProcessDocumentFileInfo(fileInfo))
      return fileInfo
    } catch (e) {
      console.error(e)
      return null
    } finally {
      dispatch(setProcessEmailAttachmentIsAttachmentLoading(false))
    }
  }, [dispatch, accountId, attachmentId, emailId, emailIntegration, canLoadAttachment, triggerLoadAttachment])

  return {
    loadAttachment,
    canLoadAttachment,
  }
}

export const useAttachmentSummaryProcessingOrchestration = (trackingEventContext: string) => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()
  const { loadAttachment, canLoadAttachment } = useLoadAttachment()
  const { calculateDocumentType, summarizeDocument: _summarizeDocument } = useDocumentSummary()

  const documentTypes = useAppSelector(selectDocumentTypes)
  const documentTypesLoading = useAppSelector(selectDocumentTypesLoading)
  const documentSummaryLoading = useAppSelector(selectDocumentSummaryExecutionLoading)
  const documentSummaryResult = useAppSelector(selectDocumentSummaryResult)

  const summarizeDocument = tracked(
    _summarizeDocument,
    {
      eventName: TrackEvent.DOCUMENT_SUMMARY_EXECUTED,
      eventDetails: {
        source: 'email_attachment',
        context: trackingEventContext,
      },
    },
    { eventDetailsExtractor: (documentType: DocumentType) => ({ fallbackToGeneric: documentType.systemDocumentTypeId === FALLBACK_DOCUMENT_TYPE_ID }) },
  )

  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null)
  const [documentType, setDocumentType] = useState<DocumentType | null>(null)

  useEffect(() => {
    if (!canLoadAttachment || fileInfo) {
      return
    }

    const invokeLoadAttachment = async () => {
      const loadedFileInfo = await loadAttachment()
      if (loadedFileInfo) {
        setFileInfo(loadedFileInfo)
      }
    }

    invokeLoadAttachment()
  }, [canLoadAttachment, fileInfo, loadAttachment])

  useEffect(() => {
    if (!fileInfo || documentTypesLoading || !documentTypes.length || documentType) {
      return
    }

    const invokeCalculateDocumentType = async () => {
      const calculatedDocumentType = await calculateDocumentType(fileInfo)
      if (calculatedDocumentType) {
        setDocumentType(calculatedDocumentType)
      }
    }

    invokeCalculateDocumentType()
  }, [fileInfo, documentTypesLoading, documentTypes.length, documentType, calculateDocumentType])

  useEffect(() => {
    if (!documentType || !fileInfo || documentSummaryLoading || documentSummaryResult) {
      return
    }

    const invokeSummarizeDocument = async () => {
      const documentSummary = await summarizeDocument(documentType, fileInfo)
      if (documentSummary) {
        setTimeout(() => dispatch(setShowDocumentSummary(true)), 500)
      }
    }

    invokeSummarizeDocument()
  }, [documentType, fileInfo, documentSummaryLoading, documentSummaryResult, summarizeDocument, dispatch])
}
